import { FormControlLabel, Grid, Paper, Switch, Divider, Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { FC, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SCUILoadingLinear } from '../../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';
import { PostCheckoutCommand } from '../../../../services/command';
import { CargoFeeManagementQuery, DeliveryQuery } from '../../../../services/query';
import TimeHelper from '../../../../utils/time';
import { CSB } from './customerShipmentBans.classes';

interface IProps {
    datas?: any;
}

export const CustomerShipmentBans: FC<IProps> = ({ datas }) => {
    const [companyList, setCompanyList] = useState<any>([]);
    const [blockedCompanyList, setBlockedCompanyList] = useState<any>();
    const [historyRecords, setHistoryRecords] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);
    const refreshList = () => {
        setRefresh((prevLoading) => !prevLoading);
    };
    const [loading, setLoading] = useState(false);

    const resultArray: any = [];
    function createArrayWithMatchingIds(arr1: any, arr2?: any) {
        try {
            if (arr1 !== null) {
                for (let i = 0; i < arr1.length; i++) {
                    if (arr2 === null) {
                        resultArray.push({
                            id: arr1[i].value,
                            isActive: false,
                            text: arr1[i].text,
                        });
                    } else if (arr1[i].value === arr2?.shippingCompanyId) {
                        resultArray.push({
                            id: arr1[i].value,
                            isActive: true,
                            text: arr1[i].text,
                        });
                    } else {
                        resultArray.push({
                            id: arr1[i].value,
                            isActive: false,
                            text: arr1[i].text,
                        });
                    }
                }
            }
            return resultArray;
        } catch {
            Swal.fire(t('general.error'), ' ', 'error');
        }
    }

    createArrayWithMatchingIds(companyList, blockedCompanyList);

    const GetCargoCompanyList = async () => {
        setLoading(true);
        try {
            const res = await CargoFeeManagementQuery.CargoCompanyList();

            if (res.data) {
                let parseData = res.data.map((item: any) => {
                    return {
                        value: item.id,
                        text: item.name,
                    };
                });

                setCompanyList(parseData);
            } else {
                Swal.fire('Service Error', res.errorMessage, 'error');
            }
        } finally {
            setLoading(false);
        }
    };
    const GetBlockedCargoCompanyList = async () => {
        try {
            let params: any = {};
            params.customerId = datas.customer.customerId;
            PostCheckoutCommand.GetByCustomerIdBlockedShippingCompanyForCustomer(params).then((res: any) => {
                if (res) {
                    setBlockedCompanyList(res.blockedShippingCompanyForCustomer);
                    createArrayWithMatchingIds(companyList, blockedCompanyList);
                } else {
                    Swal.fire('Service Error', res.message, 'error');
                }
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetBlockedShippingCompanyHistory = async () => {
        if (!datas?.customer?.customerId) return;
        setHistoryLoading(true);
        try {
            const res = await DeliveryQuery.GetBlockedShippingCompanyForCustomerHistory({
                customerId: datas.customer.customerId,
            });
            if (res?.data && Array.isArray(res.data)) {
                setHistoryRecords(
                    res.data.map((item: any, index: number) => ({
                        id: `${item.operatorId}-${item.shippingCompanyId}-${item.updateDate}-${index}`,
                        userName: item.userName || '-',
                        shippingCompanyName: item.shippingCompanyName || '-',
                        status: item.status ? t('general.active') : t('general.passive'),
                        updateDate: TimeHelper.formatDateHour(item.updateDate) || '-',
                    }))
                );
            } else {
                setHistoryRecords([]);
            }
        } catch {
            setHistoryRecords([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    // Helper function to update result array and show success message
    const updateResultArrayAndShowSuccess = (data: any, newActiveState: boolean, res: any) => {
        const index = resultArray.findIndex((item: any) => item.id === data.id);
        resultArray[index] = {
            id: data.id,
            isActive: newActiveState,
            text: data.text,
        };
        refreshList();
        Swal.fire(res.message, ' ', 'success');
    };

    // Helper function to handle blocking a shipping company
    const blockShippingCompany = (params: any, data: any) => {
        PostCheckoutCommand.UpsertBlockedShippingCompanyForCustomer(params)
            .then((res: any) => {
                updateResultArrayAndShowSuccess(data, true, res);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Helper function to handle unblocking a shipping company
    const unblockShippingCompany = (params: any, data: any) => {
        PostCheckoutCommand.DeleteBlockedShippingCompanyForCustomer(params)
            .then((res: any) => {
                updateResultArrayAndShowSuccess(data, false, res);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Helper function to process the shipping company change
    const processShippingCompanyChange = (data: any) => {
        setLoading(true);
        const params: any = {
            customerId: datas.customer.customerId,
            ShippingCompanyId: data.id,
        };

        if (data.isActive === false) {
            blockShippingCompany(params, data);
        } else {
            unblockShippingCompany(params, data);
        }
    };

    const handleUserActivityChange = (data: any) => {
        Swal.fire({
            text: t('authorizationManagement.listUser.surePassiveText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.update'),
        }).then((result: any) => {
            if (result.isConfirmed) {
                processShippingCompanyChange(data);
            }
        });
    };

    const historyColumns: GridColDef[] = useMemo(
        () => [
            { field: 'userName', headerName: t('customerInformation.userName'), flex: 1, minWidth: 150 },
            {
                field: 'shippingCompanyName',
                headerName: t('customerInformation.shippingCompanyName'),
                flex: 1,
                minWidth: 150,
            },
            { field: 'status', headerName: t('general.status'), flex: 1, minWidth: 100 },
            { field: 'updateDate', headerName: t('customerInformation.updateDate'), flex: 1, minWidth: 150 },
        ],
        [t]
    );

    useEffect(() => {
        GetCargoCompanyList();
    }, []);

    useEffect(() => {
        GetBlockedCargoCompanyList();
    }, [refresh]);

    useEffect(() => {
        GetBlockedShippingCompanyHistory();
    }, [datas?.customer?.customerId, refresh]);

    return (
        <Paper sx={{ padding: '16px' }} className={CSB.page}>
            <Grid
                container
                size={{
                    xs: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                }}
            >
                <h2 className={CSB.titleMain}>{t('customerInformation.customerShippingProhibitions')}</h2>
            </Grid>
            <Grid
                container
                sx={{ marginTop: '2rem' }}
                size={{
                    xs: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                }}
            >
                <h3 className={CSB.titleCargoCompanies}>{t('customerInformation.cargoCompanies')}</h3>
            </Grid>
            <Divider sx={{ marginTop: '1.5rem', marginBottom: '1.5rem' }} />
            <Grid
                className={CSB.switchesGrid}
                container
                sx={{ marginTop: '1.5rem' }}
                size={{
                    xs: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                }}
            >
                <SCUILoadingLinear display={loading} />
                {resultArray.map((value: any, index: number) => (
                    <Grid
                        className={CSB.switchRow}
                        key={index}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6,
                            xl: 6,
                        }}
                    >
                        <FormControlLabel
                            className={CSB.fieldCargoCompanyLabel}
                            control={
                                <Switch
                                    className={CSB.inputSwitchCargoBan}
                                    checked={value.isActive}
                                    name={value.text}
                                    onChange={() => handleUserActivityChange(value)}
                                />
                            }
                            label={value.text}
                        />
                    </Grid>
                ))}
            </Grid>
            <Grid
                container
                size={{ xs: 12, md: 12, lg: 12, xl: 12 }}
                sx={{ borderBottom: 2, marginTop: '2rem' }}
            >
                <h3 className={CSB.titleHistory}>{t('customerInformation.shippingCompanyHistory')}</h3>
            </Grid>
            <Grid
                className={CSB.historySection}
                container
                size={{ xs: 12, md: 12, lg: 12, xl: 12 }}
                sx={{ marginTop: '1.5rem' }}
            >
                <SCUILoadingLinear display={historyLoading} />
                <Box className={CSB.datagridHistory} sx={{ width: '100%' }}>
                    <SCUICustomDataGrid
                        randomId
                        rows={historyRecords}
                        columns={historyColumns}
                        pageSize={10}
                        hideFooter={historyRecords.length <= 10}
                    />
                </Box>
            </Grid>
        </Paper>
    );
};
